import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Monitor, ChefHat, Clock, CheckCircle, XCircle, RefreshCw, LogOut, Store, Bell, BellOff, Volume2, VolumeX, Timer, Package, Utensils } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatCurrency, formatTime, getTimeSince, getStatusColor, getStatusLabel } from '@/lib/utils';
import StaffSwitcher from '@/components/StaffSwitcher';

const KitchenDisplay = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  
  // Notification states
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [lastOrderCount, setLastOrderCount] = useState(0);
  const audioRef = useRef(null);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, [soundEnabled]);

  // Show browser notification
  const showBrowserNotification = useCallback((title, body) => {
    if (notificationsEnabled && 'Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/icon-192x192.png',
        tag: 'kitchen-order',
        renotify: true,
        requireInteraction: true
      });
    }
  }, [notificationsEnabled]);

  // Request notification permission
  const enableNotifications = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        toast.success('Push notifications enabled!');
      } else {
        toast.error('Notification permission denied');
      }
    }
  };

  useEffect(() => {
    fetchOutlets();
    // Check if notifications were previously enabled
    if ('Notification' in window && Notification.permission === 'granted') {
      setNotificationsEnabled(true);
    }
  }, []);

  useEffect(() => {
    if (selectedOutlet) {
      fetchKitchenOrders();
      const interval = setInterval(fetchKitchenOrders, 5000); // Refresh every 5 seconds (faster for kitchen)
      return () => clearInterval(interval);
    }
  }, [selectedOutlet]);

  const fetchOutlets = async () => {
    try {
      const response = await api.get('/outlets');
      setOutlets(response.data);
      // Auto-select first outlet if only one exists
      if (response.data.length === 1) {
        setSelectedOutlet(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching outlets:', error);
    }
  };

  const fetchKitchenOrders = async () => {
    if (!selectedOutlet) return;
    try {
      const response = await api.get(`/kitchen/orders?outlet_id=${selectedOutlet.id}`);
      const newOrders = response.data;
      
      // Check for new ONLINE orders only (not POS orders - they're already confirmed at counter)
      // POS orders have source: 'pos', online orders have source: 'online' or undefined
      const newOnlineOrderCount = newOrders.filter(o => 
        (o.status === 'placed' || o.status === 'accepted') && o.source !== 'pos'
      ).length;
      
      if (newOnlineOrderCount > lastOrderCount && lastOrderCount >= 0 && orders.length > 0) {
        playNotificationSound();
        showBrowserNotification(
          '🔔 New Online Order!',
          `${newOnlineOrderCount - lastOrderCount} new order(s) need attention`
        );
        toast.info('New online order received!', { duration: 5000 });
      }
      setLastOrderCount(newOnlineOrderCount);
      
      setOrders(newOrders);
    } catch (error) {
      console.error('Error fetching kitchen orders:', error);
    }
  };

  const markAsReady = async (orderId) => {
    setLoading(true);
    try {
      await api.put(`/kitchen/orders/${orderId}/ready`);
      toast.success('Order marked as ready! 🔥');
      fetchKitchenOrders();
    } catch (error) {
      console.error('Error marking order ready:', error);
      toast.error('Failed to update order');
    } finally {
      setLoading(false);
    }
  };

  const startPreparing = async (orderId) => {
    setLoading(true);
    try {
      await api.put(`/orders/${orderId}/status`, { status: 'preparing' });
      toast.success('Started preparing order');
      fetchKitchenOrders();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update order');
    } finally {
      setLoading(false);
    }
  };

  const getOrderPriority = (order) => {
    const createdAt = new Date(order.created_at);
    const now = new Date();
    const minutesAgo = Math.floor((now - createdAt) / 60000);
    
    if (minutesAgo > 15) return 'urgent';
    if (minutesAgo > 10) return 'high';
    return 'normal';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'border-l-red-500';
      case 'high': return 'border-l-yellow-500';
      default: return 'border-l-green-500';
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Audio element for notifications */}
      <audio ref={audioRef} src="/notification.wav" preload="auto" />
      
      {/* Header */}
      <header className="bg-[#121212] border-b border-white/10">
        <div className='container mx-auto px-4 py-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-4'>
              <Link to='/'>
                <Button variant='ghost' className='text-white'>
                  ← Back
                </Button>
              </Link>
              <ChefHat className='w-8 h-8 text-[#00C853]' strokeWidth={1.5} />
              <h1 className='text-3xl font-bold text-white' style={{ fontFamily: 'Bebas Neue' }}>
                Kitchen Display
              </h1>
            </div>
            <div className='flex items-center gap-2'>
              {/* Sound toggle */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSoundEnabled(!soundEnabled);
                  toast.info(soundEnabled ? 'Sound alerts disabled' : 'Sound alerts enabled');
                }}
                className={`border-white/20 ${soundEnabled ? 'text-green-400 border-green-400/50' : 'text-gray-500'}`}
                title={soundEnabled ? 'Sound On' : 'Sound Off'}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </Button>
              
              {/* Push Notification toggle */}
              <Button
                variant="outline"
                size="sm"
                onClick={notificationsEnabled ? () => {
                  setNotificationsEnabled(false);
                  toast.info('Push notifications disabled');
                } : enableNotifications}
                className={`border-white/20 ${notificationsEnabled ? 'text-green-400 border-green-400/50' : 'text-gray-500'}`}
                title={notificationsEnabled ? 'Notifications On' : 'Enable Notifications'}
              >
                {notificationsEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </Button>
              
              <Button 
                onClick={fetchKitchenOrders}
                variant='outline'
                className='border-white/20 text-white hover:bg-white/5'
                data-testid='refresh-orders'
              >
                <RefreshCw className='w-4 h-4 mr-2' />
                Refresh
              </Button>
              <Button
                onClick={() => {
                  logout('kitchen');
                  navigate('/');
                }}
                variant='outline'
                className='border-white/20 text-white hover:bg-red-500/10 hover:border-red-500/50'
              >
                <LogOut className='w-4 h-4 mr-2' />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      <div className='bg-[#1A1A1A] border-b border-white/10'>
        <div className='container mx-auto px-4 py-4'>
          {/* Outlet Selector - Show if multiple outlets or no outlet selected */}
          {!selectedOutlet ? (
            <div className="flex flex-col items-center py-8">
              <Store className="w-12 h-12 text-[#00C853] mb-3" />
              <h2 className="text-xl font-bold text-white mb-4">Select Kitchen Location</h2>
              <div className="flex gap-4 flex-wrap justify-center">
                {outlets.map(outlet => (
                  <Card
                    key={outlet.id}
                    onClick={() => setSelectedOutlet(outlet)}
                    className="cursor-pointer bg-[#121212] border-white/10 hover:border-[#00C853] transition-colors p-4 min-w-[200px]"
                  >
                    <h3 className="text-white font-semibold">{outlet.name}</h3>
                    <p className="text-gray-400 text-sm">{outlet.address}</p>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Outlet Info */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Store className="w-5 h-5 text-[#00C853]" />
                  <span className="text-white font-medium">{selectedOutlet.name}</span>
                  {outlets.length > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedOutlet(null)}
                      className="ml-2 border-white/20 text-white text-xs"
                    >
                      Change
                    </Button>
                  )}
                </div>
              </div>
              
              <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                <div className='text-center'>
                  <div className='text-2xl font-bold text-white'>{orders.length}</div>
                  <div className='text-sm text-gray-400'>Active Orders</div>
                </div>
                <div className='text-center'>
                  <div className='text-2xl font-bold text-yellow-400'>
                    {orders.filter(o => o.status === 'accepted').length}
                  </div>
                  <div className='text-sm text-gray-400'>Accepted</div>
                </div>
                <div className='text-center'>
                  <div className='text-2xl font-bold text-orange-400'>
                    {orders.filter(o => o.status === 'preparing').length}
                  </div>
                  <div className='text-sm text-gray-400'>Preparing</div>
                </div>
                <div className='text-center'>
                  <div className='text-2xl font-bold text-[#00C853]'>
                    {orders.filter(o => getOrderPriority(o) === 'normal').length}
                  </div>
                  <div className='text-sm text-gray-400'>On Time</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Orders Grid */}
      <div className='container mx-auto px-4 py-8'>
        {!selectedOutlet ? null : orders.length === 0 ? (
          <div className='text-center py-20'>
            <ChefHat className='w-20 h-20 mx-auto text-gray-600 mb-4' strokeWidth={1.5} />
            <p className='text-xl text-gray-400'>No active orders</p>
            <p className='text-sm text-gray-500 mt-2'>Orders will appear here when they come in</p>
          </div>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {orders.map(order => {
              const priority = getOrderPriority(order);
              return (
                <Card 
                  key={order.id} 
                  className={`order-card bg-[#121212] border-l-4 ${getPriorityColor(priority)} border-white/10`}
                  data-testid={`order-${order.id}`}
                >
                  <CardHeader className='pb-3'>
                    <div className='flex items-start justify-between'>
                      <div>
                        <CardTitle className='text-2xl text-white' style={{ fontFamily: 'JetBrains Mono' }}>
                          {order.order_number}
                        </CardTitle>
                        <div className='flex items-center gap-2 mt-2'>
                          <Badge className={getStatusColor(order.status)}>
                            {getStatusLabel(order.status)}
                          </Badge>
                          <Badge variant='outline' className='text-white border-white/20'>
                            {order.order_type.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </div>
                      </div>
                      <div className='text-right'>
                        <div className='timer text-2xl font-bold text-white'>
                          {getTimeSince(order.created_at)}
                        </div>
                        {priority === 'urgent' && (
                          <Badge className='bg-red-500/20 text-red-400 border-red-500/50 mt-2'>
                            URGENT!
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    {/* Customer Info */}
                    <div className='bg-white/5 rounded-lg p-3'>
                      <p className='text-sm text-gray-400'>Customer</p>
                      <p className='text-white font-semibold'>{order.customer_name}</p>
                      <p className='text-sm text-gray-400'>{order.customer_phone}</p>
                    </div>

                    {/* Order Items */}
                    <div className='space-y-2'>
                      <p className='text-sm text-gray-400 font-semibold'>Items:</p>
                      {order.items.map((item, idx) => (
                        <div key={idx} className={`rounded-lg p-3 ${item.is_addon_product ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-white/5'}`}>
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-2'>
                              {item.is_addon_product && (
                                <span className='text-xs bg-yellow-500/30 text-yellow-400 px-2 py-0.5 rounded-full'>Side</span>
                              )}
                              <p className='text-white font-semibold'>{item.product_name}</p>
                            </div>
                            <span className='text-lg font-bold text-[#FF4500]'>x{item.quantity}</span>
                          </div>
                          {/* Show Add-ons (Finishes) */}
                          {item.selected_addons && item.selected_addons.length > 0 && (
                            <div className='mt-2 flex flex-wrap gap-1'>
                              {item.selected_addons.map((addon, aIdx) => (
                                <span key={aIdx} className='text-xs bg-[#FF4500]/20 text-[#FF4500] px-2 py-1 rounded-full'>
                                  +{addon.name}
                                </span>
                              ))}
                            </div>
                          )}
                          {/* Show Customizations */}
                          {item.selected_customizations && item.selected_customizations.length > 0 && (
                            <div className='mt-2 flex flex-wrap gap-1'>
                              {item.selected_customizations.map((cust, cIdx) => (
                                <span key={cIdx} className='text-xs bg-[#00E5FF]/20 text-[#00E5FF] px-2 py-1 rounded-full'>
                                  {cust.group}: {cust.option}
                                </span>
                              ))}
                            </div>
                          )}
                          {/* Show Special Instructions */}
                          {item.item_notes && (
                            <div className='mt-2 bg-yellow-500/10 border border-yellow-500/30 rounded p-2'>
                              <p className='text-xs text-yellow-400'>📝 {item.item_notes}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className='flex gap-2 pt-2'>
                      {order.status === 'accepted' && (
                        <Button 
                          onClick={() => startPreparing(order.id)}
                          disabled={loading}
                          className='flex-1 bg-orange-500 hover:bg-orange-600 text-white'
                          data-testid={`start-preparing-${order.id}`}
                        >
                          <Clock className='w-4 h-4 mr-2' />
                          Start Preparing
                        </Button>
                      )}
                      {order.status === 'preparing' && (
                        <Button 
                          onClick={() => markAsReady(order.id)}
                          disabled={loading}
                          className='flex-1 bg-[#00C853] hover:bg-[#00E676] text-white'
                          data-testid={`mark-ready-${order.id}`}
                        >
                          <CheckCircle className='w-4 h-4 mr-2' />
                          Mark as Ready
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Staff Switcher - Quick switch between POS/Kitchen/Admin */}
      <StaffSwitcher />
    </div>
  );
};

export default KitchenDisplay;
